/**
 * Custom domain setup — `screens.md` §8.
 *
 * All seven of §8's states render here. Six are a pure function of one `domains` row
 * (`domain-setup.ts`); `gated` is a function of the plan with no row in service. Each is a
 * static rendering this app's suite can drive. The **traversal** between them is `e2e/`'s: `renderToStaticMarkup` fires no events
 * and runs no effects, and a static render of what lies behind a gate is not evidence the
 * gate opens.
 *
 * ## Nothing here spells the CNAME target
 *
 * `dns` arrives on the list response. `EDGE_CNAME_TARGET` is `packages/shared`'s, and a
 * second spelling in a browser bundle is the failure `apps/api/src/urls.ts` exists to
 * prevent — with the added edge that this one would be printed on packaging.
 */
import { useCallback, useEffect, useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  CUSTOM_DOMAIN_TIER,
  DOMAIN_SETUP_EXPLAINER,
  RESOLVER_NAMESPACE,
  planRank,
  type DomainSummary,
} from "@packagepage/shared";
import {
  Button,
  Pill,
  PlanGate,
  Surface,
  SurfaceBody,
  SurfaceHeader,
  Timeline,
} from "@packagepage/ui";

import { api } from "../api/procedures.js";
import { RECHECK_INTERVAL_MS } from "./artwork.js";
import { domainTimeline } from "./domain-timeline.js";
import {
  pillFor,
  EVER_LIVE_FAILING_COPY,
  progressOf,
  providerGuides,
  relativeTime,
  screenStateFor,
} from "./domain-setup.js";

import "@packagepage/ui/tokens.css";
import "@packagepage/ui/surface.css";
import "@packagepage/ui/button.css";
import "@packagepage/ui/gate.css";
import "@packagepage/ui/pill.css";
import "@packagepage/ui/input.css";
import "@packagepage/ui/timeline.css";
import "./domain-setup.css";

/**
 * One value, copied on its own.
 *
 * App-local because all three consumers are `apps/app` screens — `LinkManagement`,
 * `ProductDetail` and this one — which is **locality**, not CI cost: `packages/ui` is not on
 * `conformance.yml`'s `paths:` and costs nothing extra to touch. The third call site is what
 * earns a component; the first two stay hand-rolled until someone retrofits them.
 *
 * §8's mock: *"Each part copies on its own — most control panels want them in separate
 * boxes."* That is the whole reason this is three fields rather than one block of text.
 */
export function CopyField({
  label,
  value,
}: {
  readonly label: string;
  readonly value: string;
}): ReactNode {
  const [copied, setCopied] = useState(false);
  const copy = useCallback(() => {
    void navigator.clipboard?.writeText(value).then(() => {
      setCopied(true);
    });
  }, [value]);

  return (
    <div className="pp-domains__field">
      <span className="pp-domains__field-label">{label}</span>
      <span className="pp-domains__field-value">
        <code>{value}</code>
        <Button variant="quiet" onClick={copy} aria-label={`Copy ${label}`}>
          {copied ? "copied" : "copy"}
        </Button>
      </span>
    </div>
  );
}

/**
 * An empty state that cannot be a dead end.
 *
 * `acceptance-tests.md`: *"every empty state offers a next action. No dead ends."* `action`
 * is **required**, which is that rule expressed as a type rather than as copy review — the
 * same move `Input`'s `ValidationState` makes for rule/cause/fix.
 *
 * App-local, and owed a `packages/ui` home: `ProductList` and `ArtworkGenerator` already
 * hand-roll one each, so a shared primitive would make the rule uniform. Retrofitting two
 * screens with bespoke copy is not this unit's, and the trigger is that retrofit rather than
 * a fourth consumer — a fourth would only be more drift.
 */
export function EmptyState({
  title,
  children,
  action,
}: {
  readonly title: string;
  readonly children: ReactNode;
  readonly action: ReactNode;
}): ReactNode {
  return (
    <Surface tone="neutral" emphasis="filled">
      <div className="pp-domains__empty">
        <h2 className="pp-domains__empty-title">{title}</h2>
        {children}
        <div className="pp-domains__empty-action">{action}</div>
      </div>
    </Surface>
  );
}

/** §8's instruction card — one record, three parts, each copyable on its own. */
function RecordCard({
  host,
  target,
  brandDomain,
}: {
  readonly host: string;
  readonly target: string;
  readonly brandDomain: string;
}): ReactNode {
  return (
    <Surface sectioned>
      <SurfaceHeader
        title="Add one record at your DNS provider"
        meta="Each part copies on its own — most control panels want them in separate boxes."
      />
      <SurfaceBody>
        <CopyField label="TYPE" value="CNAME" />
        <CopyField label="HOST" value={host} />
        <CopyField label="VALUE" value={target} />
        <p className="pp-domains__permanence">
          <code>{target}</code> is our permanent routing address. It never changes,
          whatever happens to our name — so the codes you print never break.
        </p>
        {/* §8's non-negotiable preempt, in its own bordered block. The mock's rationale is
            explicit that this is the sentence a non-technical owner stalls on for a week,
            and that burying it in help text is what makes them stall. */}
        <p className="pp-domains__preempt">
          This is a brand-new subdomain. It cannot affect <code>{brandDomain}</code>, your
          website, or your email. Nothing you already have moves or changes.
        </p>
      </SurfaceBody>
    </Surface>
  );
}

/** "Where to put it" — Cloudflare and a generic Other. See `providerGuides`. */
function ProviderCard({
  host,
  target,
}: {
  readonly host: string;
  readonly target: string;
}): ReactNode {
  const guides = providerGuides(host, target);
  const [active, setActive] = useState(guides[0]?.id ?? "other");
  const shown = guides.find((guide) => guide.id === active) ?? guides[0];

  return (
    <Surface sectioned>
      <SurfaceHeader
        title="Where to put it"
        meta={
          <nav className="pp-domains__providers" aria-label="DNS provider">
            {guides.map((guide) => (
              <button
                key={guide.id}
                type="button"
                className="pp-domains__provider"
                data-active={guide.id === active ? "true" : undefined}
                aria-current={guide.id === active ? "true" : undefined}
                onClick={() => {
                  setActive(guide.id);
                }}
              >
                {guide.label}
              </button>
            ))}
          </nav>
        }
      />
      <SurfaceBody>
        <ol className="pp-domains__steps">
          {(shown?.steps ?? []).map((step) => (
            <li key={step}>{step}</li>
          ))}
        </ol>
      </SurfaceBody>
    </Surface>
  );
}

/**
 * The progress card, and the one place this screen says what it is doing on its own.
 *
 * `screens.md` §8: *"checking every 30 s"*, and *"Nothing is waiting on you."* The interval
 * is `RECHECK_INTERVAL_MS`, shared with the artwork screen's blocked state, so the two
 * cannot claim different numbers.
 *
 * **The notify-me §8 draws is not built, and the absence is stated rather than mimed with a
 * dead input** — c92's ruling, unchanged: there is no mail transport anywhere in this
 * repository, and a person against a print deadline who submits an address and waits for a
 * message nobody will send has been given a reason not to take the escape hatch.
 */
function ProgressCard({ domain }: { readonly domain: DomainSummary }): ReactNode {
  const steps = domainTimeline(progressOf(domain), {
    added: "On your account, and the address your codes would carry.",
    record:
      "Usually minutes, occasionally a few hours — it depends on your provider, not on us.",
    certificate: "Automatic. Nothing for you to do.",
    serving:
      domain.first_live_at === null
        ? "The moment this happens, print files carry your own address — permanently."
        : `First served ${relativeTime(domain.first_live_at, new Date())}.`,
  });

  return (
    <Surface sectioned>
      <SurfaceHeader
        title="Progress"
        meta={`checking every ${String(RECHECK_INTERVAL_MS / 1000)} s`}
      />
      <SurfaceBody>
        <Timeline steps={steps} />
        <p className="pp-domains__note">
          Nothing is waiting on you. Leave this open and it updates itself — we cannot
          email you when it goes live, because this product has no mail transport yet.
        </p>
      </SurfaceBody>
    </Surface>
  );
}

/** §8 state D — found-vs-wanted, as a diff rather than a complaint. */
function FoundVsWanted({
  found,
  wanted,
  host,
  brandDomain,
}: {
  readonly found: string | null;
  readonly wanted: string;
  readonly host: string;
  readonly brandDomain: string;
}): ReactNode {
  return (
    <Surface tone="error" emphasis="filled">
      <div className="pp-domains__diff">
        <h2>
          {found === null
            ? "We could not find a record"
            : "We found a record, but it points somewhere else"}
        </h2>
        {found !== null && (
          <dl className="pp-domains__diff-rows">
            <div>
              <dt>We found</dt>
              <dd>
                <code>{found}</code>
              </dd>
            </div>
            <div>
              <dt>Should be</dt>
              <dd>
                <code>{wanted}</code>
              </dd>
            </div>
          </dl>
        )}
        {/* The three causes §8 names, and the mock is explicit that the third is the one
            nobody guesses and the most common. */}
        <p>
          Usually one of three things: the record was added to the wrong domain, an old
          record for <code>{host}</code> already existed and needs replacing, or the host
          field was typed as <code>{`${host}.${brandDomain}`}</code> where your provider
          only wants <code>{host}</code>.
        </p>
        <p className="pp-domains__unaffected">
          Your codes are unaffected — they are still answering on our address.
        </p>
      </div>
    </Surface>
  );
}

/** §8 state G — the speed bump with a seatbelt. */
function EverLiveFailing({ domain }: { readonly domain: DomainSummary }): ReactNode {
  return (
    <Surface tone="warn" emphasis="filled">
      <div className="pp-domains__failing">
        <h2>{EVER_LIVE_FAILING_COPY.headline}</h2>
        <p>
          {domain.first_live_at !== null && (
            <>
              This domain has been live since{" "}
              {relativeTime(domain.first_live_at, new Date())}, so the setup is right —
              something is wrong right now.{" "}
            </>
          )}
          {EVER_LIVE_FAILING_COPY.reassurance}
        </p>
        {/* The printed count and the last look, together: what is in the field, and how
            fresh that claim is. A count with no timestamp reads as live telemetry. */}
        <p className="pp-domains__printed">
          {domain.printed_sku_count === 1
            ? "1 product has print files carrying this address"
            : `${String(domain.printed_sku_count)} products have print files carrying this address`}
          {domain.last_probe_at !== null && (
            <> — last checked {relativeTime(domain.last_probe_at, new Date())}</>
          )}
          .
        </p>
        {/* PERMANENCE_SENTENCE earns its call site here: first_live_at is set, so codes
            carrying this hostname are printed and in the field, and removal is exactly what
            a frustrated reader reaches for. */}
        <p className="pp-domains__permanence">{EVER_LIVE_FAILING_COPY.permanence}</p>
      </div>
    </Surface>
  );
}

/** §8 state E — steered, never blocked. */
function ApexSteer({ hostname }: { readonly hostname: string }): ReactNode {
  return (
    <Surface tone="warn" emphasis="filled">
      <div className="pp-domains__apex">
        <h2>Use a subdomain, not {hostname} itself</h2>
        {/* The mock's rationale: lead with the consequence they care about — their website
            going down — never with the DNS technicality. */}
        <p>
          Pointing your whole domain at us would take your website down, and bare domains
          cannot use CNAME records at all — so the setup is fragile in ways you would only
          discover later.
        </p>
        <p>
          <code>id.{hostname}</code> is the convention. It is shorter on a label than a
          path, and it leaves your website alone.
        </p>
      </div>
    </Surface>
  );
}

/**
 * §8 state E — adding a hostname is Starter's. Rendered only with no domain in service, so
 * a downgraded organisation keeps its live domain's screen (Q-P4).
 */
export function DomainGate(): ReactNode {
  return (
    <div data-screen-label="Domain setup · gated">
      <PlanGate
        plan={CUSTOM_DOMAIN_TIER}
        feature="Your own domain comes with Starter"
        why={`${DOMAIN_SETUP_EXPLAINER.ownership} If a press date is coming, do this first.`}
        instead="Codes printed with our address answer on every plan, permanently, and we will never take that away."
      />
    </div>
  );
}

/** §8 state A — the explainer that sells ownership, composed from `packages/shared`. */
function PreSetup({ onAdd }: { readonly onAdd: (hostname: string) => void }): ReactNode {
  const [hostname, setHostname] = useState("");
  return (
    <EmptyState
      title={DOMAIN_SETUP_EXPLAINER.headline}
      action={
        <form
          className="pp-domains__add"
          onSubmit={(event) => {
            event.preventDefault();
            onAdd(hostname.trim());
          }}
        >
          <label className="pp-domains__add-label" htmlFor="pp-domains-hostname">
            The address your codes will answer on
          </label>
          <input
            id="pp-domains-hostname"
            className="pp-domains__add-input"
            name="hostname"
            value={hostname}
            placeholder="id.yourbrand.com"
            onChange={(event) => {
              setHostname(event.target.value);
            }}
          />
          <Button variant="primary" type="submit">
            Set it up
          </Button>
        </form>
      }
    >
      {/* Composed, never re-typed: §8 requires this sentence and the artwork escape hatch's
          to be word-identical, and c91 put it in packages/shared for this screen. */}
      <p>{DOMAIN_SETUP_EXPLAINER.ownership}</p>
      {/* Owed by U63 before anyone points DNS at us — the namespace Decision. */}
      <p>{RESOLVER_NAMESPACE}</p>
      <p className="pp-domains__empty-note">
        One record, about two minutes. We will show you exactly what to type and check it
        for you.
      </p>
    </EmptyState>
  );
}

export function Domains(): ReactNode {
  const client = useQueryClient();
  const domains = useQuery({ queryKey: ["domains"], queryFn: api.domains });
  const org = useQuery({
    queryKey: ["orgs", "current"],
    queryFn: () => api.currentOrg(),
  });
  const invalidate = useCallback(() => {
    void client.invalidateQueries({ queryKey: ["domains"] });
  }, [client]);

  const create = useMutation({ mutationFn: api.createDomain, onSuccess: invalidate });
  const recheck = useMutation({ mutationFn: api.recheckDomain, onSuccess: invalidate });
  const remove = useMutation({ mutationFn: api.removeDomain, onSuccess: invalidate });

  const domain = domains.data?.domains[0];
  const target = domains.data?.dns.value ?? "";

  // §8's "checking every 30 s", and the mock's own rationale for it: "Nothing is waiting on
  // you… you can close this." An interval rather than a button is the whole promise.
  useEffect(() => {
    if (domain === undefined) return;
    // A live row REFETCHES the list and issues no DoH recheck. Until U16 this returned and
    // a live screen went stale forever; a recheck would be the wrong remedy, because
    // `nextStatus`'s `live` arm is a no-op, so it would spend a DoH lookup to learn nothing
    // and could not surface the watch's counter, which is what state G reads.
    const live = domain.status === "live";
    const id = setInterval(() => {
      if (live) invalidate();
      else recheck.mutate(domain.id);
    }, RECHECK_INTERVAL_MS);
    return () => {
      clearInterval(id);
    };
  }, [domain, recheck, invalidate]);

  if (domains.isPending)
    return <p data-screen-label="Domains loading">Loading domains…</p>;

  if (domain === undefined) {
    if (org.isPending) return <p data-screen-label="Domains loading">Loading domains…</p>;
    if (
      org.data !== undefined &&
      planRank(org.data.plan) < planRank(CUSTOM_DOMAIN_TIER)
    ) {
      return <DomainGate />;
    }
    return (
      <section data-screen-label="Domain setup · before you start">
        <PreSetup
          onAdd={(hostname) => {
            create.mutate({ hostname });
          }}
        />
      </section>
    );
  }

  const state = screenStateFor(domain);
  const host = domain.hostname.split(".")[0] ?? domain.hostname;
  const brandDomain = domain.hostname.split(".").slice(1).join(".") || domain.hostname;

  return (
    <section className="pp-domains" data-screen-label={`Domain setup · ${state}`}>
      <header className="pp-domains__header">
        <div>
          <h1 className="pp-domains__hostname">{domain.hostname}</h1>
          <div className="pp-domains__meta">
            <Pill family="domain" value={pillFor(domain.status)}>
              {domain.status === "pending_dns" ? "Waiting for DNS" : domain.status}
            </Pill>
            <span>
              Added {relativeTime(domain.created_at, new Date())} · {domain.kind}
            </span>
          </div>
        </div>
        <div className="pp-domains__actions">
          <Button
            onClick={() => {
              recheck.mutate(domain.id);
            }}
            loading={recheck.isPending}
          >
            Check again
          </Button>
          <Button
            variant="destructive"
            onClick={() => {
              remove.mutate(domain.id);
            }}
            loading={remove.isPending}
          >
            Remove
          </Button>
        </div>
      </header>

      {state === "apex-steered" && <ApexSteer hostname={domain.hostname} />}
      {state === "error-never-served" && (
        <FoundVsWanted
          found={domain.last_dns_answer}
          wanted={target}
          host={host}
          brandDomain={brandDomain}
        />
      )}
      {state === "ever-live-failing" && <EverLiveFailing domain={domain} />}
      {state === "live" && (
        <Surface tone="live" emphasis="filled">
          <div className="pp-domains__live">
            <h2>{domain.hostname} is answering</h2>
            <p>
              Certificate issued, all four checks passed. Print files you make from now on
              carry your own address.
            </p>
          </div>
        </Surface>
      )}

      <div className="pp-domains__body">
        <div className="pp-domains__main">
          <RecordCard host={host} target={target} brandDomain={brandDomain} />
          <ProviderCard host={host} target={target} />
        </div>
        <aside className="pp-domains__aside">
          <ProgressCard domain={domain} />
        </aside>
      </div>
    </section>
  );
}
